import { Flex, MantineStyleProps, Table } from '@mantine/core';
import { SelectMetadata } from 'src/data-capture/types';
import {
  BaseFinancialItem,
  PensionAsset,
} from 'src/financial/financial-data/hooks/useFinancialItems';
import { DynamicUiEntitiesMetadata } from 'src/UI/dynamicUI/types';

type FinancialItemGenericTable<T extends BaseFinancialItem> = {
  financialItems: T[];
  fieldsRenderingRules: {
    headerTitle: string;
    render: (item: T) => string | undefined;
  }[];
  pl?: MantineStyleProps['pl'];
  pr?: MantineStyleProps['pr'];
  onRowClick: (clickedItem: BaseFinancialItem) => void;
};

export const formatDate = (date: string | undefined) => {
  if (!date) {
    return '';
  }
  const wrappedDate = new Date(date);
  return `${wrappedDate.getDate().toString().padStart(2, '0')}/${(wrappedDate.getMonth() + 1)
    .toString()
    .padStart(2, '0')}/${wrappedDate.getFullYear()}`;
};
export const getFinancialItemType = (
  asset: BaseFinancialItem,
  metadata: DynamicUiEntitiesMetadata,
) => {
  function isPensionAsset(targetAsset: BaseFinancialItem): targetAsset is PensionAsset {
    return 'pensionProduct' in targetAsset;
  }

  const typeMetadata = isPensionAsset(asset)
    ? metadata[asset.pensionProduct]?.type
    : metadata[asset.product]?.type;

  return (typeMetadata as SelectMetadata).options.find(o => o.value === asset.type)?.label;
};

export function FinancialItemGenericTable<T extends BaseFinancialItem>({
  financialItems,
  fieldsRenderingRules,
  pl = 50,
  pr = 112,
  onRowClick,
}: FinancialItemGenericTable<T>) {
  const rows = financialItems.map(financialItem => {
    return (
      <Table.Tr key={financialItem.id} onClick={() => onRowClick(financialItem)}>
        {fieldsRenderingRules.map(({ headerTitle, render }, index) => {
          const isLastItem = index === fieldsRenderingRules.length - 1;
          return index === 0 ? (
            <Table.Td key={headerTitle} pl={pl}>
              <Flex direction={'row'} gap={'xxs'}>
                <img
                  src={financialItem.logoUri}
                  height={20}
                  width={20}
                  alt={financialItem.description}
                />
                {render(financialItem)}
              </Flex>
            </Table.Td>
          ) : (
            <Table.Td
              key={headerTitle}
              pr={isLastItem ? pr : undefined}
              ta={isLastItem ? 'right' : undefined}
            >
              {render(financialItem)}
            </Table.Td>
          );
        })}
      </Table.Tr>
    );
  });

  return (
    <>
      <Table withRowBorders={false} variant="accordion-table">
        <Table.Thead>
          <Table.Tr>
            {fieldsRenderingRules.map(({ headerTitle }, index) => (
              <Table.Th
                key={headerTitle}
                pl={index === 0 ? pl : undefined}
                ta={index === fieldsRenderingRules.length - 1 ? 'right' : undefined}
                pr={index === fieldsRenderingRules.length - 1 ? pr : undefined}
              >
                {headerTitle}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.length > 0 ? (
            rows
          ) : (
            <Table.Tr data-empty-row data-testid="empty-row">
              <Table.Td colSpan={fieldsRenderingRules.length} ta="center">
                No items added
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </>
  );
}
